import React, { FunctionComponent, ReactElement, useCallback } from 'react'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import FontelloIcon from '@sport1/react-elements/FontelloIcon'
import Image from '../Image'
import { TestID } from '../../utils/PropTypesDefinitions'
import { BackgroundColorTypes, TextColorTypes } from '../../Theme/Fonts/FontTypes'
import LabelFont from '../../Theme/Fonts/Label'
import { RequireOnlyOne } from '../../utils/types/requireOnlyOne'
import FlagImage from '../../BaseComponents/Image/Flag'
import PlayerImage from '../../BaseComponents/Image/Player'

type IconProps = {
    name: string
    fontSize?: number
    color?: string
    marginTop?: number
    marginBottom?: number
}

export type Props = {
    /** choose between opacity mode or background mode in active state
     *
     * @defaultValue OPACITY
     */
    type?: 'OPACITY' | 'BACKGROUND'
    /** url of image to show inside of the chip */
    imageUrl?: string
    /** Text to show inside of the chip */
    text?: string
    /** info about imageUrl for seo */
    alt?: string
    /** shows chip in active color */
    active?: boolean
    /** gives the icon a fixed color */
    tintColor?: 'black' | 'white'
    /** backgroundColor for active chip
     *
     * @defaultValue keshi-3
     */
    activeBackgroundColor?: BackgroundColorTypes
    /** fontColor for active chip
     *
     * @defaultValue fontSurface
     */
    activeFontColor?: TextColorTypes
    /** backgroundColor for in-active chip
     *
     * @defaultValue tahitian-4
     */
    inActiveBackgroundColor?: BackgroundColorTypes
    /** fontColor for in-active chip
     *
     * @defaultValue fontInactive
     */
    inActiveFontColor?: TextColorTypes
    /** size of the image */
    imageSize?: number
    /**
     * callback function triggered when chip is pressed
     *
     * @TJS-type function
     */
    onPress?: () => void
    /** Icon and it's props to show inside of the chip */
    iconProps?: IconProps
    /**
     *  The Image type determines aspect ratio and radius of the picture
     *  * @defaultValue DEFAULT
     * */
    imageType?: 'DEFAULT' | 'FLAG' | 'PLAYER'
    /**
     *  The opacity of the component
     *  * @defaultValue 1
     * */
    opacity?: number
} & TestID

export type FilterChipProps = RequireOnlyOne<Props, 'imageUrl' | 'text' | 'iconProps'>

const FilterChip: FunctionComponent<FilterChipProps> = ({
    type = 'OPACITY',
    imageUrl,
    text,
    alt,
    active = false,
    onPress,
    tintColor,
    activeBackgroundColor = 'onyx',
    activeFontColor = 'BLACK',
    inActiveBackgroundColor = 'tahitian-4',
    inActiveFontColor = 'keshi-3',
    imageSize = 22,
    iconProps,
    testID,
    imageType,
    opacity = 1,
}): ReactElement => {
    const renderImage = useCallback((): ReactElement => {
        if (imageType === 'FLAG')
            return <FlagImage imageUrl={imageUrl} size="S" width={imageSize} alt={alt} />
        if (imageType === 'PLAYER')
            return <PlayerImage imageUrl={imageUrl} size="S" width={imageSize} alt={alt} />
        return (
            <Image
                tintColor={tintColor}
                imageUrl={imageUrl}
                width={imageSize}
                alt={alt}
                size="S"
                aspectRatio="1:1"
            />
        )
    }, [alt, imageSize, imageType, imageUrl, tintColor])

    const component = (
        <Grid
            noExpand
            center
            width={44}
            height={44}
            borderRadius={22}
            backgroundColor={
                active && type === 'BACKGROUND' ? activeBackgroundColor : inActiveBackgroundColor
            }
            elevation={active ? 3 : 0}
            opacity={opacity}
        >
            {imageUrl && renderImage()}
            {iconProps ? (
                <FontelloIcon
                    name={iconProps.name}
                    color={iconProps.color}
                    fontSize={iconProps.fontSize}
                    marginTop={iconProps.marginTop}
                    marginBottom={iconProps.marginBottom}
                />
            ) : null}
            {text ? (
                <Grid noExpand width={44} paddingX={1}>
                    <LabelFont
                        bold
                        color={active ? activeFontColor : inActiveFontColor}
                        numberOfLines={1}
                        textAlign="center"
                    >
                        {text}
                    </LabelFont>
                </Grid>
            ) : null}
        </Grid>
    )
    return (
        <Grid noExpand center height={48} testID={testID}>
            {onPress ? <Touchable onPress={onPress}>{component}</Touchable> : component}
        </Grid>
    )
}

export default FilterChip
